import { ComparePageTemplate, type CompareContent } from './ComparePageTemplate';

const content: CompareContent = {
  competitor: 'Lever',
  competitorSegment: 'Mid-market ATS + CRM (Employ Inc.)',
  tagline:
    'Lever excels at CRM-driven sourcing. GoGrabJob Business goes further — adding AI interviews and explainable scoring that Lever cannot match without third-party add-ons.',
  intro:
    "Lever built its reputation on a clean recruiter experience that blends ATS pipeline management with candidate relationship management. It remains a strong choice for teams where nurturing passive candidates is the primary motion. But structured assessment, AI interviews, and agency multi-client support are not Lever's strengths. GoGrabJob Business covers all of it natively — and comes with India-ready pricing.",

  tableGroups: [
    {
      groupName: 'General',
      rows: [
        { feature: 'Free trial / starter plan',        ours: 'yes',         theirs: 'no'         },
        { feature: 'Transparent public pricing',       ours: 'yes',         theirs: 'no'         },
        { feature: 'Starting price',                   ours: 'Free → ₹999', theirs: '$3,500+/yr' },
        { feature: 'India / INR pricing',              ours: 'yes',         theirs: 'no'         },
        { feature: 'Setup & go-live time',             ours: '< 48 hours',  theirs: '2–4 weeks'  },
        { feature: 'Mobile-friendly interface',        ours: 'yes',         theirs: 'partial'    },
      ],
    },
    {
      groupName: 'Core ATS',
      rows: [
        { feature: 'Applicant tracking pipeline',      ours: 'yes',  theirs: 'yes'     },
        { feature: 'Job posting & career page',        ours: 'yes',  theirs: 'yes'     },
        { feature: 'Structured interview kits',        ours: 'yes',  theirs: 'yes'     },
        { feature: 'Interview scheduling',             ours: 'yes',  theirs: 'yes'     },
        { feature: 'Offer management',                 ours: 'yes',  theirs: 'yes'     },
        { feature: 'Approval workflows',               ours: 'yes',  theirs: 'yes'     },
        { feature: 'Requisition management',           ours: 'yes',  theirs: 'yes'     },
      ],
    },
    {
      groupName: 'CRM & Sourcing',
      rows: [
        { feature: 'Candidate CRM / talent pool',      ours: 'yes',  theirs: 'yes'     },
        { feature: 'Nurture sequences & drip emails',  ours: 'yes',  theirs: 'yes'     },
        { feature: 'LinkedIn Recruiter integration',   ours: 'yes',  theirs: 'yes'     },
        { feature: 'Chrome sourcing extension',        ours: 'partial', theirs: 'yes'  },
        { feature: 'Referral management',              ours: 'yes',  theirs: 'yes'     },
      ],
    },
    {
      groupName: 'AI & Assessment',
      rows: [
        { feature: 'AI video + voice interviews',       ours: 'yes',               theirs: 'no'     },
        { feature: 'Explainable role-fit scoring',      ours: 'Gap-level analysis', theirs: 'no'     },
        { feature: 'Resume AI parsing & ranking',       ours: 'yes',               theirs: 'partial' },
        { feature: 'Structured scoring rubrics',        ours: 'yes',               theirs: 'yes'     },
        { feature: 'Multilingual AI (EN/HI/Hinglish)',  ours: 'yes',               theirs: 'no'      },
        { feature: 'Automated candidate shortlisting',  ours: 'yes',               theirs: 'no'      },
      ],
    },
    {
      groupName: 'Agency & Multi-client',
      rows: [
        { feature: 'Agency client mode',                ours: 'yes',  theirs: 'no'  },
        { feature: 'Client-branded submission packs',   ours: 'yes',  theirs: 'no'  },
        { feature: 'Multi-account management',          ours: 'yes',  theirs: 'no'  },
      ],
    },
    {
      groupName: 'Reporting & Compliance',
      rows: [
        { feature: 'DEI / EEOC analytics',      ours: 'yes',  theirs: 'yes'    },
        { feature: 'Full audit trail',           ours: 'yes',  theirs: 'yes'    },
        { feature: 'GDPR tooling & DPA',         ours: 'yes',  theirs: 'yes'    },
        { feature: 'SSO (SAML / OIDC)',          ours: 'yes',  theirs: 'yes'    },
        { feature: 'Custom dashboards',          ours: 'yes',  theirs: 'partial' },
      ],
    },
  ],

  metrics: [
    { value: '50%',   label: 'Faster time-to-hire',   sub: 'AI screening eliminates manual phone-screen rounds' },
    { value: '40%',   label: 'Less admin per hire',   sub: 'workflow automation vs manual follow-up' },
    { value: '3×',    label: 'Roles screened / week', sub: 'with the same number of recruiters' },
  ],

  deepDive: [
    {
      title: 'Lever does CRM well — but assessment is where it stops',
      body: "Lever's strength is the recruiter experience for proactive sourcing: building talent pools, sending nurture sequences, and keeping warm candidates in a CRM view. That workflow is genuinely polished. But when an application comes in, Lever routes it into a pipeline and relies on humans to screen, phone-interview, and score. GoGrabJob Business adds what happens next: an AI interview runs automatically for every applicant, producing a scored assessment your recruiter reviews in minutes rather than scheduling calls for hours. The result is that your sourcing efforts actually convert faster.",
      highlight: "Lever gets candidates in the pipeline. GoGrabJob Business gets them screened, scored, and shortlisted — automatically.",
    },
    {
      title: 'AI interviews remove the phone screen bottleneck entirely',
      body: "The phone screen is the highest-volume, lowest-value activity in most recruiting funnels. A recruiter spending 30 minutes per screen across 40 applicants is investing 20 hours before a single candidate reaches the hiring manager. GoGrabJob Business runs AI interviews for every applicant in parallel — the first 40 responses are available for review within hours of the application deadline. Recruiters shift their time from conducting to evaluating: reviewing AI-scored assessments, validating top candidates, and advancing the shortlist to the panel.",
    },
    {
      title: 'Explainable scoring builds hiring manager confidence',
      body: "Lever's scorecard system allows structured feedback during live interviews but does not produce a pre-screen fit score. When a recruiter presents candidates to a hiring manager in Lever, the rationale is essentially 'I think these five are worth your time.' GoGrabJob Business shows the hiring manager a role-fit score with the competency breakdown, the candidate's answers, and the AI evaluation for each criterion. This turns a subjective shortlist into an evidence-based recommendation — something every hiring manager can interrogate, validate, and ultimately trust.",
    },
    {
      title: 'GoGrabJob serves the Indian market that Lever was not built for',
      body: "Lever is priced in USD and built for the US and UK markets. Its AI capabilities do not support Hindi or Hinglish — two languages that are central to Indian professional communication. GoGrabJob Business was designed with the Indian market as a first-class citizen: INR pricing, multilingual AI interviews, and data residency in Mumbai. For Indian recruitment agencies, in-house teams at Indian companies, and global companies with India hiring programmes, GoGrabJob Business is the platform that actually fits the context.",
    },
  ],

  ourAdvantages: [
    'AI video + voice interviews run automatically for every applicant',
    'Explainable role-fit scoring with gap analysis — no black box',
    'Native agency client mode and branded submission packs',
    'INR pricing — accessible for Indian teams without USD contracts',
    'Multilingual AI interviews in English, Hindi, and Hinglish',
    'Live in 48 hours — no weeks-long implementation project',
    'Visual workflow automation on all paid plans',
  ],

  theirAdvantages: [
    'Superior passive sourcing and candidate CRM nurture tooling',
    'More polished Chrome extension for sourcing from LinkedIn',
    'Larger North American customer base and case study library',
    'Deeper referral management and employee referral portal',
    'Stronger brand for US mid-market enterprise procurement',
  ],

  faqs: [
    {
      q: "Can I keep Lever's CRM while using GoGrabJob for AI interviews?",
      a: "GoGrabJob Business is a complete ATS with its own candidate CRM, so you would replace Lever rather than run it alongside. However, if your team is deeply invested in Lever's sourcing extension and does not want to migrate immediately, our API allows you to pipe applications from external sources — including Lever — into GoGrabJob AI interviews. Contact our solutions team for a migration plan.",
    },
    {
      q: "How long does it take to migrate from Lever to GoGrabJob?",
      a: "Most teams complete the migration in 2–3 weeks: one week to configure GoGrabJob Business (roles, pipelines, interview templates), one week to import existing candidate data from Lever's CSV export, and a parallel running period where both systems are live. We provide a migration checklist and a dedicated onboarding call for paid accounts.",
    },
    {
      q: "Does GoGrabJob Business support the nurture and drip-email sequences I use in Lever?",
      a: "Yes. GoGrabJob Business includes email sequences and templates for candidate communication across every pipeline stage. You can set up automated follow-up sequences triggered by stage changes, AI interview completion, or time elapsed. Campaign analytics show open rates, reply rates, and stage conversion per sequence.",
    },
    {
      q: "How does GoGrabJob handle Lever's talent pool / CRM features?",
      a: "GoGrabJob Business has a candidate CRM module where profiles are created from any source — applied, sourced, referred, or imported. Tags, custom fields, interview history, and email threads are all visible on a unified candidate record. The main difference from Lever is that every candidate record in GoGrabJob Business can be enriched with an AI interview assessment — giving you signal beyond a resume and an email sequence.",
    },
  ],
};

export function VsLeverPage() {
  return <ComparePageTemplate content={content} />;
}
